import {
    ECEntitySubtype,
    ECJuridicalType,
    ECLegalForm,
    ECMemberPersonalData,
    ECMode,
    ECPodUtilityType,
    ECSubjectType,
    EnergyCommunityNamedPod,
    EntitySubType,
    PlantIncentiveMode,
    PlantStatisticalProfile,
    PodStatisticalProfile
} from 'smag-commons/cfg/hierarchy';
import {errorCode, getContractualPowerValues, isBlank, isDefined} from '../commons/commons';
import {ECPodsStatistics} from 'smag-commons/dto/eco';
import {FormControl, FormGroup} from '@angular/forms';
import {Moment} from 'moment-timezone';
import {LOWER_INCENTIVE_BOUNDARY, UPPER_INCENTIVE_BOUNDARY} from './ec-economics';
import {EnergyCommunityAddress} from 'smag-commons/cfg/hierarchy/ec';
import {ErrorCode} from '../api/errors';
import {
    ResetContext as StatisticalProfileResetContext,
    StatisticalProfilePlantResource,
    StatisticalProfilePodResourceType,
    StatisticalProfileResourceType
} from './statistical-profile/statistical-profile.component';
import {PredictiveModelPlantResource, PredictiveModelResourceType} from './predictive-model/predictive-model.component';
import {EnumValue} from './common';
import {PredictiveModelInfo} from 'smag-commons/cfg/prediction';
import {Phases, VoltageLevel} from 'smag-commons/cfg';
import {PodStatisticalProfileRequestPlantType} from 'smag-commons/dto/api/profiles';

/**
 * Builds a view-friendly name for the given energy community {@link ECMember member}.
 *
 * @param member The member (or a member-like interface with relevant personal data)
 * @returns string View-friendly name for the member
 */
export function mkMemberName(member: {
    personal_data: {
        first_name?: string,
        last_name?: string,
        company_name?: string
    }
}): string {
    const pd = member.personal_data || {} as ECMemberPersonalData;
    if (isBlank(pd.first_name) && isBlank(pd.last_name) && !isBlank(pd.company_name)) {
        return pd.company_name.trim();
    }
    return [pd.first_name, pd.last_name, pd.company_name ? `(${pd.company_name})` : null]
        .filter(n => !isBlank(n))
        .map(n => n.trim())
        .join(' ');
}

export function mkMode(stats: ECPodsStatistics): ECMode {
    if (stats.statistical_active_count + stats.statistical_inactive_count > 0 && stats.monitored_active_count + stats.monitored_inactive_count === 0) {
        return ECMode.BASE;
    } else if (stats.statistical_active_count + stats.statistical_inactive_count === 0 && stats.monitored_active_count + stats.monitored_inactive_count > 0) {
        return ECMode.ADVANCED;
    } else if (stats.statistical_active_count + stats.statistical_inactive_count > 0 && stats.monitored_active_count + stats.monitored_inactive_count > 0) {
        return ECMode.HYBRID;
    } else {
        return null;
    }
}


const PHYSICAL_POD_UTILITY_TYPES = {
    [ECLegalForm.ENTREPRENEUR_SOLE_PROPRIETORSHIP_AGRICULTURAL_ENTERPRISE]: [ECPodUtilityType.OTHER],
    [ECLegalForm.OTHER_INDIVIDUALS]: [ECPodUtilityType.RESIDENTIAL]
};

const JURIDICAL_POD_UTILITY_TYPES = {
    [ECSubjectType.TERRITORIAL_BODY_LOCAL_AUTHORITY_OTHER_BODY]: {
        [ECEntitySubtype.STATE]: [ECPodUtilityType.BUSINESS_OFFICE, ECPodUtilityType.OTHER],
        [ECEntitySubtype.REGION]: [ECPodUtilityType.BUSINESS_OFFICE, ECPodUtilityType.OTHER],
        [ECEntitySubtype.PROVINCE]: [ECPodUtilityType.BUSINESS_OFFICE, ECPodUtilityType.OTHER],
        [ECEntitySubtype.MUNICIPALITY]: [ECPodUtilityType.BUSINESS_OFFICE, ECPodUtilityType.OTHER],
        [ECEntitySubtype.MUNICIPALITIES_UNION]: [ECPodUtilityType.BUSINESS_OFFICE, ECPodUtilityType.OTHER],
        [ECEntitySubtype.MOUNTAIN_COMMUNITY]: [ECPodUtilityType.BUSINESS_OFFICE, ECPodUtilityType.OTHER],
        [ECEntitySubtype.ISLAND_COMMUNITY]: [ECPodUtilityType.BUSINESS_OFFICE, ECPodUtilityType.OTHER],
        [ECEntitySubtype.METROPOLITAN_CITY]: [ECPodUtilityType.BUSINESS_OFFICE, ECPodUtilityType.OTHER],
        [ECEntitySubtype.RESEARCH_AND_TRAINING_ORGANIZATION]: [ECPodUtilityType.PUBLIC_SCHOOL_OR_RESEARCH_INSTITUTION, ECPodUtilityType.LIBRARY_OR_MUSEUM,
            ECPodUtilityType.OTHER],
        [ECEntitySubtype.RELIGIOUS_ORGANIZATION]: [ECPodUtilityType.RELIGIOUS_INSTITUTION],
        [ECEntitySubtype.THIRD_SECTOR_ORGANIZATION]: [ECPodUtilityType.THIRD_SECTOR_ORGANIZATION_OR_ENVIRONMENT_PROTECTION_ORGANIZATION],
        [ECEntitySubtype.ENVIRONMENT_PREVENTION_ORGANIZATION]: [ECPodUtilityType.THIRD_SECTOR_ORGANIZATION_OR_ENVIRONMENT_PROTECTION_ORGANIZATION],
        [ECEntitySubtype.LOCAL_ADMINISTRATION_ORGANIZATION]: [ECPodUtilityType.BUSINESS_OFFICE, ECPodUtilityType.PUBLIC_HEALTHCARE_FACILITY, ECPodUtilityType.OTHER]
    },
    [ECSubjectType.ASSOCIATION_WITH_LEGAL_PERSONALITY_UNDER_PRIVATE_LAW]: [ECPodUtilityType.BUSINESS_OFFICE],
    [ECSubjectType.COMMUNITY]: null,  // unsupported
    [ECSubjectType.OTHER]: [ECPodUtilityType.OTHER],
    [ECSubjectType.ASSOCIATED_FIRM_COMPANY_OF_PROFESSIONALS]: [ECPodUtilityType.BUSINESS_OFFICE],
    [ECSubjectType.PMI]: [
        ECPodUtilityType.STORE, ECPodUtilityType.GYM, ECPodUtilityType.RESTAURANT, ECPodUtilityType.BUSINESS_OFFICE,
        ECPodUtilityType.LARGE_DISTRIBUTION, ECPodUtilityType.PRIVATE_HEALTHCARE_FACILITY, ECPodUtilityType.HOTEL,
        ECPodUtilityType.SME_FACTORY, ECPodUtilityType.OTHER
    ],
};

export function getPodUtilityTypes(juridicalType: ECJuridicalType, legalForm: ECLegalForm, subjectType: ECSubjectType,
                                   entitySubtype: ECEntitySubtype): ECPodUtilityType[] {
    if (juridicalType === ECJuridicalType.INDIVIDUAL) {
        return PHYSICAL_POD_UTILITY_TYPES[legalForm];
    } else if (juridicalType === ECJuridicalType.CORPORATE_ENTITY) {
        if (subjectType === ECSubjectType.TERRITORIAL_BODY_LOCAL_AUTHORITY_OTHER_BODY) {
            if (entitySubtype) {
                return (JURIDICAL_POD_UTILITY_TYPES[subjectType])[entitySubtype];
            }
            return [ECPodUtilityType.OTHER];  // return OTHER if we have no entity subtype set
        }
        return JURIDICAL_POD_UTILITY_TYPES[subjectType];
    } else {
        console.error('juridical type not selected');
        return [];
    }
}


export enum PodEnergyProfiles {
    CONSUMER = 'consumer',
    PRODUCER = 'producer',
    PROSUMER = 'prosumer'
}

export const POD_TYPES: { id: PodEnergyProfiles, label: string }[] = [
    {id: PodEnergyProfiles.CONSUMER, label: 'Consumer'},
    {id: PodEnergyProfiles.PROSUMER, label: 'Prosumer'},
    {id: PodEnergyProfiles.PRODUCER, label: 'Producer'},
];

export function getPodEnergyProfile(pod: EnergyCommunityNamedPod): PodEnergyProfiles {
    if (pod.plants?.length > 0) {
        return pod.pod.producer ? PodEnergyProfiles.PRODUCER : PodEnergyProfiles.PROSUMER;
    }
    return PodEnergyProfiles.CONSUMER;
}

export function getEnergyProfile(pod: EnergyCommunityNamedPod): EnergyProfiles {
    if (pod.plants?.length > 0) {
        return pod.pod.producer ? EnergyProfiles.PRODUCER : EnergyProfiles.PROSUMER;
    }
    return EnergyProfiles.CONSUMER;
}

export function getLabeledProfile(pod: EnergyCommunityNamedPod): string {
    const profile = getPodEnergyProfile(pod);
    return POD_TYPES.find(pt => pt.id === profile).label;
}


export function getIncentiveTypes(plantForm: FormGroup, updateDefaultValue: boolean = false): [PlantIncentiveMode[], boolean] { // [mat select options, mat select disabled]
    // plant with no date: incentive mode cannot be chosen, it must be 'No incentive'
    if (!hasDate(plantForm)) {
        setDefaultIncentiveType(PlantIncentiveMode.NO_INCENTIVE);
        return [[PlantIncentiveMode.NO_INCENTIVE], true];
    }
    const plantDate = (plantForm.controls.enterData.value as Moment).valueOf();
    // plant with date <= LOWER_INCENTIVE_BOUNDARY: incentive mode cannot be chosen, it must be 'No incentive'
    if (plantDate <= LOWER_INCENTIVE_BOUNDARY) {
        setDefaultIncentiveType(PlantIncentiveMode.NO_INCENTIVE);
        return [[PlantIncentiveMode.NO_INCENTIVE], true];
    }
    // plant with date > LOWER_INCENTIVE_BOUNDARY and < UPPER_INCENTIVE_BOUNDARY: incentive mode can be chosen from 'No incentive', 'New regulations' and 'Old regulations'. Default -> 'Old regulations'
    if (plantDate > LOWER_INCENTIVE_BOUNDARY && plantDate < UPPER_INCENTIVE_BOUNDARY) {
        setDefaultIncentiveType(PlantIncentiveMode.OLD_REGULATIONS);
        return [[PlantIncentiveMode.NO_INCENTIVE, PlantIncentiveMode.NEW_REGULATIONS, PlantIncentiveMode.OLD_REGULATIONS], false];
    }
    // plant with date >= UPPER_INCENTIVE_BOUNDARY: incentive mode can be chosen from 'No incentive' and 'New regulations'. Default -> 'New regulations'
    if (plantDate >= UPPER_INCENTIVE_BOUNDARY) {
        setDefaultIncentiveType(PlantIncentiveMode.NEW_REGULATIONS);
        return [[PlantIncentiveMode.NO_INCENTIVE, PlantIncentiveMode.NEW_REGULATIONS], false];
    }

    function setDefaultIncentiveType(incentive: PlantIncentiveMode) {
        if (!plantForm.controls.incentiveType.value || updateDefaultValue) {
            plantForm.controls.incentiveType.setValue(incentive);
        }
    }
}

export function hasDate(form: FormGroup): boolean {
    return !!form.controls.enterData.value;
}

const COUNTRY = 'Italia';

/**
 * example: Strada Martinella, 172, 43124 Alberi, Parma PR, Italia
 * @param address to be converted to string
 */
export function addressToString(address: EnergyCommunityAddress): string {
    if (!address) {
        return '';
    }
    const formattedAddress = [address.street, address.street_number, address.postal_code, address.municipality, address.province]
        .filter(isDefined)
        .join(', ');
    return address.province !== COUNTRY ? `${formattedAddress}, ${COUNTRY}` : formattedAddress;
}

export function mkTextField(value: string): string {
    const tv = value ? value.trim() : '';
    return tv.length ? tv : undefined;
}


export interface PlantType {
    name: string;
}

export const PV_PLANT_TYPE: PlantType = {name: 'Photovoltaic plant'};
export const WIND_PLANT_TYPE: PlantType = {name: 'Wind plant'};
export const PLANT_TYPES: PlantType[] = [PV_PLANT_TYPE, WIND_PLANT_TYPE];


export function mkErrorMessage(err: Error, fallback: string): string {
    const code = errorCode(err);
    switch (code) {
        case ErrorCode.POD_CONNECTED_TO_NEW_ENTITY_NOT_IN_COMMUNITY:
        case ErrorCode.POD_CONNECTED_TO_ENTITY_NOT_IN_COMMUNITY:
            return 'POD connects resource to a member that is not part of this Energy Community';
        default:
            return fallback;
    }
}

export function mkStatisticalProfilePodType(energyProfile: EnergyProfiles): StatisticalProfilePodResourceType {
    switch (energyProfile) {
        case EnergyProfiles.CONSUMER:
            return StatisticalProfilePodResourceType.CONSUMER;
        case EnergyProfiles.PRODUCER:
            return StatisticalProfilePodResourceType.PRODUCER;
        case EnergyProfiles.PROSUMER:
            return StatisticalProfilePodResourceType.PROSUMER;
    }
}


export function getStatisticalProfileResourceType(type: PlantType): StatisticalProfileResourceType.PV_PLANT | StatisticalProfileResourceType.WIND_PLANT {
    switch (type) {
        case WIND_PLANT_TYPE:
            return StatisticalProfileResourceType.WIND_PLANT;
        case PV_PLANT_TYPE:
        default:
            return StatisticalProfileResourceType.PV_PLANT;
    }
}

export function getPredictiveModelResourceType(type: PlantType): PredictiveModelResourceType.PV_PLANT | PredictiveModelResourceType.WIND_PLANT {
    switch (type) {
        case WIND_PLANT_TYPE:
            return PredictiveModelResourceType.WIND_PLANT;
        case PV_PLANT_TYPE:
        default:
            return PredictiveModelResourceType.PV_PLANT;
    }
}

export function getPodStatisticalProfileRequestPlantType(type: PlantType): PodStatisticalProfileRequestPlantType.PV | PodStatisticalProfileRequestPlantType.WIND {
    switch (type) {
        case WIND_PLANT_TYPE:
            return PodStatisticalProfileRequestPlantType.WIND;
        case PV_PLANT_TYPE:
        default:
            return PodStatisticalProfileRequestPlantType.PV;
    }
}

export function getEntitySubtype(type: PlantType): EntitySubType.WIND_PLANT | EntitySubType.PV_PLANT {
    switch (type) {
        case WIND_PLANT_TYPE:
            return EntitySubType.WIND_PLANT;
        case PV_PLANT_TYPE:
        default:
            return EntitySubType.PV_PLANT;
    }
}

export function getContractualPowerEnum(currentValue: number): EnumValue {
    const contractualPowerValues = getContractualPowerValues();
    const index = contractualPowerValues.findIndex(cpv => cpv.value === currentValue);
    return index >= 0 ? contractualPowerValues[index] : null;
}

export function mkPodStatisticalProfileResetValueProvider(): (ctx: StatisticalProfileResetContext) => Promise<PodStatisticalProfile> {
    return () => Promise.resolve(null); // reset statistical profile for POD (consumer or prosumer)
}

export function isControlOK(form: FormGroup, controlName: string) {
    return form.controls[controlName].dirty && !form.controls[controlName].valid;
}

export function getPlantName(form: FormGroup) {
    return form.controls.plantName.value as string;
}

export function getNominalPower(form: FormGroup) {
    return form.controls.nominalPower.value;
}

export function getStatisticalProfile(form: FormGroup) {
    return form.controls.profile.value as PlantStatisticalProfile;
}

export function getPredictiveModel(form: FormGroup) {
    return form.controls.predictiveModel.value as PredictiveModelInfo;
}

export function getEnterData(form: FormGroup) {
    return form.controls.enterData.value;
}

export function getPodCode(form: FormGroup) {
    return form.controls.podCode.value;
}

export function getPlantType(form: FormGroup) {
    return form.controls.plantType.value;
}

export function mkStatisticalProfilePlantResource(plantForm: FormGroup): StatisticalProfilePlantResource {
    return {
        type: getStatisticalProfileResourceType(plantForm.controls.plantType.value),
        name: getPlantName(plantForm),
        nominalPower: getNominalPower(plantForm),
        profile: getStatisticalProfile(plantForm)
    };
}

export function mkPredictiveModelPlantResource(plantForm: FormGroup): PredictiveModelPlantResource {
    return {
        type: getPredictiveModelResourceType(plantForm.controls.plantType.value),
        nominalPower: getNominalPower(plantForm),
        predictiveModel: getPredictiveModel(plantForm)
    };
}

export const POD_VOLTAGES = [
    {value: VoltageLevel.LOW, name: 'Low__{voltage}'},
    {value: VoltageLevel.MEDIUM, name: 'Medium__{voltage}'}
    // {value: VoltageLevel.HIGH, name: 'High__{voltage}'}
];
export const POD_PHASES = [
    {value: Phases.SINGLE, name: 'Single phase'},
    {value: Phases.THREE, name: 'Three phase'}
];

export function getFormControl(fg: FormGroup, controlName: string): FormControl {
    return fg.controls[controlName] as FormControl;
}

export enum EnergyProfiles {
    CONSUMER = 'Consumer',
    PROSUMER = 'Prosumer',
    PRODUCER = 'Producer'
}

